import { useState, useCallback } from "react";

import dayjs from "dayjs";

import {
  faCheck,
  faQuestionCircle,
  faReceipt,
  faClockRotateLeft,
  faShareNodes,
  faCircleQuestion,
  faCircleCheck,
  faBan
} from "@fortawesome/free-solid-svg-icons";
import { faEye } from "@fortawesome/pro-solid-svg-icons";

import {
  sumTonsCo2e,
  splitAggregatedTransactionsBreakdownSubcategories
} from "@aclymatepackages/other-helpers";
import mainTheme from "@aclymatepackages/themes";
import findSubcategory from "@aclymatepackages/subcategories";

import {
  useCachedFirebaseCrud,
  useAccountData,
  useCollectionDataListener,
  auth
} from "../firebase";
import { fetchOurApi } from "../utils/apiCalls";
import { getAccountId } from "../accountHelpers";
import { analyticsTrack } from "../analytics";
import usePlatformLayoutContext from "../contexts/platformLayout";
import useExplorerLayoutContext from "../contexts/explorerLayout";
import usePermissions from "../hooks/usePermissions";

export const EARLIEST_BUYER_SURVEY_YEAR = 2021;

export const vendorStatuses = {
  confirmed: {
    id: "confirmed",
    icon: faCheck,
    severity: 0,
    tooltip: "This vendor is confirmed and has confirmed transactions",
    name: "Confirmed Vendors",
    color: mainTheme.palette.secondary.main
  },
  uncategorized: {
    id: "uncategorized",
    severity: 2,
    tooltip:
      "This vendor hasn't been categorized so their emissions haven't been added to your balance.",
    name: "Uncategorized Vendors",
    icon: faQuestionCircle,
    color: mainTheme.palette.error.main
  },
  noTransactions: {
    id: "noTransactions",
    icon: faReceipt,
    tooltip:
      "This vendor has been categorized but doesn't have any transactions",
    name: "No transactions",
    color: mainTheme.palette.error.light,
    severity: 1
  },
  unconfirmed: {
    id: "unconfirmed",
    tooltip:
      "This vendor hasn't been confirmed yet so their emissions aren't as accurate as they could be.",
    icon: faEye,
    name: "Unconfirmed",
    color: mainTheme.palette.error.dark,
    severity: 3
  }
};

export const surveyDataStatusMap = {
  requested: {
    label: "Requested",
    icon: faClockRotateLeft,
    color: mainTheme.palette.warning.dark,
    chipColor: "warning",
    tooltip: "You've requested this vendor's emissions data."
  },
  pending: {
    label: "Shared — awaiting confirmation",
    icon: faShareNodes,
    color: mainTheme.palette.info.main,
    chipColor: "info",
    tooltip: "This vendor has shared their emissions data. Review and accept."
  },
  unconfirmed: {
    label: "Unconfirmed",
    icon: faCircleQuestion,
    color: mainTheme.palette.text.secondary,
    chipColor: "default",
    tooltip: "Survey completed but data not yet shared."
  },
  accepted: {
    label: "Accepted",
    icon: faCircleCheck,
    color: mainTheme.palette.secondary.main,
    chipColor: "success",
    tooltip: "You've accepted this vendor's emissions data."
  },
  revoked: {
    label: "Revoked",
    icon: faBan,
    color: mainTheme.palette.error.main,
    chipColor: "error",
    tooltip: "This vendor has revoked their shared emissions data."
  }
};

export const updateSurveyDataEntry = (surveyData, year, updates) => {
  const existing = (surveyData || []).find(
    (entry) => Number(entry.year) === Number(year)
  );
  if (existing) {
    return (surveyData || []).map((entry) =>
      Number(entry.year) === Number(year) ? { ...entry, ...updates } : entry
    );
  }
  return [...(surveyData || []), { year: Number(year), ...updates }];
};

export const getAvailableSurveyYears = (vendorSurveys = []) => {
  const lastCompleteYear = dayjs().year() - 1;
  if (!vendorSurveys.length) {
    return [lastCompleteYear];
  }
  const completedYears = vendorSurveys.map(({ year }) => Number(year));
  const minYear = Math.min(...completedYears, lastCompleteYear);
  const maxYear = Math.max(...completedYears, lastCompleteYear);
  return Array.from({ length: maxYear - minYear + 1 }, (_, i) => maxYear - i);
};

export const getAggregateSurveyStatus = (surveyData = []) => {
  const nonRevokedEntry = [...surveyData]
    .sort((a, b) => Number(b.year) - Number(a.year))
    .find(({ status }) => status && status !== "revoked");
  if (nonRevokedEntry) return nonRevokedEntry.status;
  const hasAnyEntry = surveyData.some(({ status }) => !!status);
  if (hasAnyEntry) return "revoked";
  return null;
};

export const getSurveyYearsForBuyer = (startDate, accountingYears = []) => {
  const companyStartYear = startDate
    ? dayjs(startDate?.toDate ? startDate.toDate() : startDate).year()
    : EARLIEST_BUYER_SURVEY_YEAR;
  const startYear = Math.max(companyStartYear, EARLIEST_BUYER_SURVEY_YEAR);
  const currentYear = dayjs().year();
  const lockedYears = new Set(
    accountingYears.map(({ date }) => {
      const d = date?.toDate ? date.toDate() : date;
      return dayjs(d).year();
    })
  );
  return Array.from(
    { length: Math.max(currentYear - startYear, 0) },
    (_, i) => currentYear - 1 - i
  ).filter((year) => !lockedYears.has(year));
};

export const deriveSurveyCompletion = ({ vendorSurveys = [] } = {}) => {
  const completedYears = vendorSurveys.map(({ year: y }) => Number(y));
  const mostRecentSurveyYear = completedYears.length
    ? Math.max(...completedYears)
    : null;
  const availableYears = [...completedYears].sort((a, b) => b - a);
  const hasSurveyData = mostRecentSurveyYear !== null;
  return {
    hasSurveyData,
    mostRecentSurveyYear,
    availableYears
  };
};

export const buildSurveyDataFromVendorSurvey = (vendorSurvey) => {
  const { tonsCo2ePerDollar, emissionCategory, industryNaics, industryName } =
    vendorSurvey;
  return {
    tonsCo2ePerDollar,
    emissionCategory,
    naicsCode: industryNaics,
    naicsTitle: industryName
  };
};

export const isVendorSurveyYearIncomplete = (vendorSurveys, year) => {
  const numericYear = Number(year);
  const vendorSurvey = (vendorSurveys || []).find(
    ({ year: y }) => Number(y) === numericYear
  );
  if (!vendorSurvey) {
    return false;
  }
  const skippedFields = vendorSurvey.skippedFields || [];
  const unknownFields = vendorSurvey.unknownFields || [];
  return skippedFields.length > 0 || unknownFields.length > 0;
};

export const useShareCustomerYear = () => {
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [accountData] = useAccountData();
  const [customerRelationships] = useCollectionDataListener("customers");
  const { activateSnackbar } = useExplorerLayoutContext() || {};
  const { canShare } = usePermissions();

  return useCallback(
    async ({ relationshipId, year }) => {
      if (!canShare) {
        activateSnackbar?.({
          severity: "error",
          message: "You don't have permission to share data with customers."
        });
        return;
      }
      if (!relationshipId || year === undefined || year === null) {
        activateSnackbar?.({
          severity: "error",
          message: "Couldn't share — missing relationship or year."
        });
        return;
      }
      const numericYear = Number(year);
      const relationship = (customerRelationships || []).find(
        ({ id }) => id === relationshipId
      );
      if (!relationship) {
        activateSnackbar?.({
          severity: "error",
          message: "Couldn't find that customer. Refresh and try again?"
        });
        return;
      }
      const vendorSurveys = accountData?.vendorSurveys || [];
      const vendorSurvey = vendorSurveys.find(
        ({ year: y }) => Number(y) === numericYear
      );
      const isSurveyCompleteForYear =
        vendorSurvey?.tonsCo2ePerDollar !== undefined;
      if (!isSurveyCompleteForYear) {
        window.location.href = `/explorer/emissions-survey?year=${numericYear}`;
        return;
      }
      if (isVendorSurveyYearIncomplete(vendorSurveys, numericYear)) {
        activateSnackbar?.({
          severity: "warning",
          message: `Your ${numericYear} survey still has skipped or unknown answers. Complete it before sharing with ${relationship.buyerName || "this customer"}.`
        });
        return;
      }
      const existingSurveyData = relationship.surveyData || [];
      const existingEntry = existingSurveyData.find(
        ({ year: y }) => Number(y) === numericYear
      );
      if (existingEntry?.status === "accepted") {
        activateSnackbar?.({
          severity: "info",
          message: `${relationship.buyerName || "This customer"} has already accepted your ${numericYear} share.`
        });
        return;
      }
      if (existingEntry?.status === "pending") {
        activateSnackbar?.({
          severity: "info",
          message: `Your ${numericYear} data is already shared with ${relationship.buyerName || "this customer"} — they're reviewing it.`
        });
        return;
      }
      const buyerLabel = relationship.buyerName || "your customer";
      const optimisticSurveyData = updateSurveyDataEntry(
        existingSurveyData,
        numericYear,
        {
          ...buildSurveyDataFromVendorSurvey(vendorSurvey),
          status: "pending"
        }
      );
      analyticsTrack("scout_customer_share_clicked", {
        relationshipId,
        year: numericYear
      });
      await updateCollectionDoc("vendor-relationships", relationshipId, {
        surveyData: optimisticSurveyData
      });
      try {
        const response = await fetchOurApi({
          path: "/vendors/share-vendor-survey-data",
          method: "POST",
          data: { relationshipId, year: numericYear, share: true },
          callback: (res) => res
        });
        if (!response?.success) {
          await updateCollectionDoc("vendor-relationships", relationshipId, {
            surveyData: existingSurveyData
          });
          activateSnackbar?.({
            severity: "error",
            message: `Couldn't share with ${buyerLabel}. Try again?`
          });
          return;
        }
        activateSnackbar?.({
          severity: "success",
          message: `Shared your ${numericYear} emissions data with ${buyerLabel}. They can revoke or you can revoke any time.`
        });
      } catch (err) {
        console.error("[shareCustomerYear] backend call threw", err);
        await updateCollectionDoc("vendor-relationships", relationshipId, {
          surveyData: existingSurveyData
        });
        activateSnackbar?.({
          severity: "error",
          message: `Couldn't share with ${buyerLabel}. Check your connection and try again.`
        });
      }
    },
    [
      canShare,
      customerRelationships,
      accountData,
      updateCollectionDoc,
      activateSnackbar
    ]
  );
};

const findVendorStatus = ({
  emissionCategory,
  transactions,
  isConfirmed,
  tonsCo2ePerDollar,
  scope,
  naicsCode
}) => {
  if (!isConfirmed) {
    return vendorStatuses.unconfirmed;
  }

  const isVendorUncategorized = () => {
    if (!emissionCategory) {
      return true;
    }

    if (emissionCategory === "spend-based") {
      return !naicsCode || !tonsCo2ePerDollar;
    }

    if (scope === 3 && !tonsCo2ePerDollar) {
      return true;
    }

    return false;
  };

  if (isVendorUncategorized()) {
    return vendorStatuses.uncategorized;
  }

  return vendorStatuses.confirmed;
};

export const formatVendors = ({
  vendorsWithRelationships,
  matchedVendorTransactions,
  transactionsMonthsData,
  emissionsSum
}) => {
  const findVendorSurveyStatus = (
    survey,
    surveyData,
    vendorContacts,
    hasExplorerAccount
  ) => {
    const aggregateStatus = getAggregateSurveyStatus(surveyData);

    if (aggregateStatus) {
      return `survey-${aggregateStatus}`;
    }

    const { dateCompleted } = survey || {};

    if (dateCompleted) {
      return "survey-unconfirmed";
    }

    const anyContactInvited = (vendorContacts || []).some(
      ({ explorerInvitedAt }) => !!explorerInvitedAt
    );
    const isInvited = anyContactInvited || (survey && !dateCompleted);

    if (isInvited && hasExplorerAccount) {
      return "has-explorer-account";
    }

    if (isInvited) {
      return "invited";
    }

    return "not-invited";
  };

  const extractAndSumVendorTransactions = (aggregatedTransactions) => {
    const vendorTransactions =
      splitAggregatedTransactionsBreakdownSubcategories(aggregatedTransactions);

    const totalTransactionsCount = vendorTransactions.reduce(
      (acc, { transactionsCount }) => acc + transactionsCount,
      0
    );

    const tonsCo2e = sumTonsCo2e(vendorTransactions);

    return {
      transactions: vendorTransactions,
      transactionsCount: totalTransactionsCount,
      tonsCo2e,
      avgTonsCo2ePerTransaction: tonsCo2e / (totalTransactionsCount || 1)
    };
  };

  const formatVendorsWithRelationships = (relationship) => {
    const {
      id: relationshipId,
      vendorId,
      vendorName,
      nickname,
      vendorData,
      survey,
      surveyData,
      vendorContacts,
      hasExplorerAccount,
      ...otherProps
    } = relationship;
    const { scopeThreeCategory = 1 } = vendorData || {};

    const { aggregatedTransactions = [] } =
      matchedVendorTransactions.find(
        ({ companyId }) => companyId === vendorId
      ) || {};

    const vendorTransactionProps = extractAndSumVendorTransactions(
      aggregatedTransactions
    );

    const displayName = getVendorDisplayName({ name: vendorName, nickname });

    return {
      ...otherProps,
      id: relationshipId,
      name: displayName,
      canonicalName: vendorName,
      nickname,
      vendorId,
      hasExplorerAccount,
      surveyStatus: findVendorSurveyStatus(
        survey,
        surveyData,
        vendorContacts,
        hasExplorerAccount
      ),
      vendorSurveyStatus: findVendorSurveyStatus(
        survey,
        surveyData,
        vendorContacts,
        hasExplorerAccount
      ),
      survey,
      surveyData,
      vendorContacts,
      ...{ ...vendorData, scopeThreeCategory },
      ...vendorTransactionProps
    };
  };

  const formattedVendors = vendorsWithRelationships.map((relationship) => {
    const formattedVendor = formatVendorsWithRelationships(relationship);

    const { id: status, severity } = findVendorStatus(formattedVendor);

    return {
      ...formattedVendor,
      status,
      severity
    };
  });

  const totalAggregatedTransactionsCount = transactionsMonthsData.reduce(
    (sum, { transactionsCount = 0 }) => sum + transactionsCount,
    0
  );

  const vendorsWithTransactions = formattedVendors.filter(
    ({ transactionsCount }) => transactionsCount
  );

  const totalVendorTonsCo2e = vendorsWithTransactions.reduce(
    (sum, { tonsCo2e }) => sum + tonsCo2e,
    0
  );

  return formattedVendors.map((vendor) => {
    const { transactionsCount, tonsCo2e } = vendor;

    return {
      ...vendor,
      transactionsCountPercentage: transactionsCount
        ? (transactionsCount / totalAggregatedTransactionsCount) * 100
        : 0,
      totalTonsCo2ePercentage: (tonsCo2e / emissionsSum) * 100,
      typeTonsCo2ePercentage: (tonsCo2e / totalVendorTonsCo2e) * 100
    };
  });
};

export const archiveVendorTransactions = async (vendorId) => {
  const companyId = getAccountId();

  return await fetchOurApi({
    path: "/transactions/archive-vendor-transactions",
    method: "POST",
    data: {
      companyId,
      vendorId
    }
  });
};

export const useOnVendorSave = (callback = () => {}) => {
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();

  const [saveLoading, setSaveLoading] = useState(false);

  const onVendorSave = async (vendor) => {
    const {
      emissionCategory,
      scopeThreeCategory,
      naicsCode,
      naicsTitle,
      id,
      tonsCo2ePerDollar: vendorTonsCo2ePerDollar
    } = vendor;

    const companyId = getAccountId();

    const updateScopeProps = () => {
      if (emissionCategory === "spend-based") {
        return { scopeThreeCategory, scope: 3 };
      }

      const { scope, scopeThreeCategory: subcategoryScopeThreeCategory } =
        findSubcategory(emissionCategory);

      if (scope === 3) {
        return { scopeThreeCategory: subcategoryScopeThreeCategory, scope };
      }
      return { scopeThreeCategory: null, scope };
    };

    const buildEmissionsCategoriesProps = () => {
      if (emissionCategory === "spend-based") {
        return {
          naicsCode,
          naicsTitle,
          tonsCo2ePerDollar: vendorTonsCo2ePerDollar
        };
      }

      const { naics } = findSubcategory(emissionCategory);
      const { tonsCo2ePerDollar } = naics || {};
      return { tonsCo2ePerDollar };
    };

    const vendorData = {
      emissionCategory,
      ...updateScopeProps(),
      ...buildEmissionsCategoriesProps(),
      isConfirmed: true,
      confirmedBy: "user",
      confirmedAt: new Date(),
      confirmedByUserId: auth.currentUser?.uid || null
    };

    setSaveLoading(true);

    updateCollectionDoc("vendor-relationships", id, { vendorData });

    await fetchOurApi({
      path: "/transactions/update-vendor-transactions",
      method: "POST",
      data: {
        companyId,
        ...vendor,
        ...vendorData
      },
      callback: () => triggerTransactionUpdateCountdown()
    });

    return callback();
  };

  return { saveLoading, onVendorSave, setSaveLoading };
};

export const isVendorUncategorized = ({ emissionCategory, naicsCode }) => {
  if (!emissionCategory) {
    return true;
  }
  if (emissionCategory === "spend-based") {
    return !naicsCode;
  }
  return false;
};

export const useOnVendorUncategorize = (callback = () => {}) => {
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const { triggerTransactionUpdateCountdown } = usePlatformLayoutContext();

  const [uncategorizeLoading, setUncategorizeLoading] = useState(false);

  const onVendorUncategorize = async (vendor) => {
    const { id, vendorId } = vendor;
    const companyId = getAccountId();

    const clearedVendorData = {
      emissionCategory: null,
      naicsCode: null,
      naicsTitle: null,
      scopeThreeCategory: null,
      tonsCo2ePerDollar: 0,
      isConfirmed: true,
      uncategorizedBy: "user",
      uncategorizedAt: new Date(),
      uncategorizedByUserId: auth.currentUser?.uid || null
    };

    setUncategorizeLoading(true);

    updateCollectionDoc("vendor-relationships", id, {
      vendorData: clearedVendorData
    });

    await fetchOurApi({
      path: "/transactions/uncategorize-vendor-transactions",
      method: "POST",
      data: {
        companyId,
        vendorId,
        id
      },
      callback: () => triggerTransactionUpdateCountdown()
    });

    return callback();
  };

  return {
    uncategorizeLoading,
    onVendorUncategorize,
    setUncategorizeLoading
  };
};

// Utility function to get the display name for a vendor
// Returns nickname if available, otherwise returns the canonical name
export const getVendorDisplayName = (vendor) => {
  if (!vendor) {
    return "";
  }
  return vendor.nickname || vendor.name || "";
};

// Utility function to get both display name and canonical name for contexts where both are needed
export const getVendorNames = (vendor) => {
  if (!vendor) return { displayName: "", canonicalName: "" };
  return {
    displayName: vendor.nickname || vendor.name || "",
    canonicalName: vendor.name || "",
    nickname: vendor.nickname || null
  };
};
